import { useState, useCallback, useMemo } from 'react'
import { omit } from 'ramda'

import Dropdown from 'app/common/ui/Dropdown'
import i18n, { changeLanguage, availableLanguages, isLang } from 'app/i18n'
import { popupErr } from 'app/common/actions/popup'

import LangItem, { type LangItemProps } from './LangItem'
import Flag from './flags/Flag'

import styles from './LangSwitcher.module.scss';

const langs: Array<LangItemProps & { key: string }> = availableLanguages.map((key) => ({
  key,
  id: key,
}))

const defaultOptions = {
  dx: 7,
  dy: 10,
}

type Props = {
  className?: string
}

const LangSwitcher = ({ className }: Props) => {
  const [isOpen, setOpenState] = useState(false)
  const toggle = useCallback(() => {
    setOpenState((state) => !state)
  }, [])

  const [activeId, setActiveId] = useState(i18n.language)

  const [activeItemProps, items] = useMemo(
    () => [langs.find(({ id }) => id === activeId), langs.filter(({ id }) => id !== activeId)],
    [activeId],
  )

  const handleChangeLng = useCallback(
    async (lng: string) => {
      if (!isLang(lng)) return
      try {
        await changeLanguage(lng)
        setActiveId(lng)
      } catch (error) {
        console.error(error)
        popupErr(i18n.t('ui:lang_change_error'))
      }
      toggle()
    },
    [toggle],
  )

  // const { key: activeKey, ...activeItemPropsWithoutKey } = activeItemProps || { key: null, id: null }

  return (
    <div className={className}>
      <div className={styles.selector}>
        <div className={styles.label}>{`${i18n.t('ui:lang_change_label')}:`}</div>
        <Dropdown
          isOpen={isOpen}
          toggle={toggle}
          options={defaultOptions}
          contentClassName={styles.content}
          content={
            <>
              {activeItemProps && (
                <LangItem
                  key={activeItemProps.key}
                  {...omit(['key'], activeItemProps)}
                  onClick={toggle}
                  active
                />
              )}
              {items.map(({ key, ...props }) => (
                <LangItem key={key} {...props} onClick={handleChangeLng} />
              ))}
            </>
          }
        >
          <Flag lng={activeId} />
        </Dropdown>
      </div>
    </div>
  )
}

export default LangSwitcher
